/**
 * Section of the customer-history sidebar — either Offers or Orders.
 * Renders a title + a <ul> of CommandItemView children driven by a
 * CustomerCommands collection, with a graceful empty-state inside the
 * list when the collection is empty.
 *
 * Fetches the status list for its `commandType` ('offer' | 'command') via
 * `orders:statuses` and uses it for two things:
 *   - populating each row's inline status <select>;
 *   - sorting rows by the status's `displayOrder` so the sidebar groups
 *     offers/orders in the same order the kanban columns use.
 *
 * Marionette.CompositeView: the template is the section frame, the
 * collection drives childViewContainer.
 */
define('salesInboxCommandsListView',[
  'app',
  'module',
  'marionette',
  'underscore',
  'jquery',
  'settings',
  'ordersMailsUtils',
  'salesInboxCommandItemView'
], function (App, Module, Marionette, _, $, Settings, OrdersMailsUtils, CommandItemView) {
  'use strict';

  // Supplier / invoice templates have dedicated send flows (and recipients)
  // inside OrdersMailsUtils.sendMail, so they don't belong in a customer-facing
  // picker. Mirrors the per-row list in commands/item/view.js.
  var NON_CUSTOMER_TEMPLATES = {
    MAIL_ORDER_SUPPLIER: true,
    MAIL_ORDER_SUPPLIER_REQUEST: true,
    MAIL_ORDER_INVOICE: true
  };

  function customerTemplates() {
    return _.chain(Settings.configValue('mails.templates') || [])
      .filter(function (t) { return t && t.template && !NON_CUSTOMER_TEMPLATES[t.template]; })
      .map(function (t) {
        return { value: t.template, label: _.i18n('mails.template.' + t.template) };
      })
      .value();
  }

  // Empty-state placeholder shown inside the <ul> when the collection has
  // no rows. Stays consistent with the <li>-based list DOM so SCSS
  // doesn't need a special case.
  function buildEmptyView(message) {
    return Marionette.ItemView.extend({
      tagName: 'li',
      className: 'empty',
      template: function () { return message; },
      serializeData: function () { return {}; }
    });
  }

  Module.exports = Marionette.CompositeView.extend({
    className: 'list-block',

    template: _.template(
      '<h5><%= title %></h5>' +
      '<% if (selectable) { %>' +
      '<div class="cmd-bulk<%= selectedCount ? " on" : "" %>">' +
      '  <label class="cmd-selall" title="Select all">' +
      '    <input type="checkbox" class="selall-cb">' +
      '    <span class="cmd-bulk-count"><%= selectedCount ? (selectedCount + " selected") : "Select" %></span>' +
      '  </label>' +
      '  <select class="cmd-bulk-status" title="Set status for the selected items">' +
      '    <option value="">Set status…</option>' +
      '    <% _.each(statuses, function (s) { %><option value="<%= s.id %>"><%= s.label %></option><% }); %>' +
      '  </select>' +
      '  <% if (templates && templates.length) { %>' +
      '  <select class="cmd-bulk-tpl" title="Send one mail template for the selected items">' +
      '    <option value="">✉ Template…</option>' +
      '    <% _.each(templates, function (t) { %><option value="<%= t.value %>"><%= t.label %></option><% }); %>' +
      '  </select>' +
      '  <% } %>' +
      '  <button type="button" class="cmd-bulk-clear" title="Clear selection">✕</button>' +
      '</div>' +
      '<% } %>' +
      '<ul class="cmd-list"></ul>'
    ),

    childView: CommandItemView,
    childViewContainer: '.cmd-list',

    // Marionette uses this to order child views on render / sort / add,
    // independent of the collection's own comparator. We return a stable
    // (displayOrder, -createDate) tuple: statuses in the order the server
    // returned them (which is the configured displayOrder), rows inside a
    // status bucket newest-first.
    viewComparator: function (a, b) {
      var byStatus = this.displayOrderByStatus || {};
      var oa = byStatus[a.get('status')];
      var ob = byStatus[b.get('status')];
      // Unknown statuses (not returned by the config) go to the bottom so
      // they don't randomly intermix with configured ones.
      if (oa === undefined) { oa = Number.MAX_SAFE_INTEGER; }
      if (ob === undefined) { ob = Number.MAX_SAFE_INTEGER; }
      if (oa !== ob) { return oa - ob; }

      var da = a.get('createDate') || a.get('date');
      var db = b.get('createDate') || b.get('date');
      var ta = da ? new Date(da).getTime() : 0;
      var tb = db ? new Date(db).getTime() : 0;
      return tb - ta;
    },

    // When a row's status changes inline, Backbone collections bubble the
    // model's 'change:status' up here. Re-sort so the row jumps to its
    // new displayOrder bucket.
    collectionEvents: {
      'change:status': 'onStatusChanged'
    },

    onStatusChanged: function () {
      if (!this.collection) { return; }
      // Triggering the collection's 'sort' event is enough — Marionette's
      // _sortViews handler will pick up the new viewComparator ordering
      // and re-render children in place.
      this.collection.trigger('sort');
    },

    initialize: function (options) {
      options = options || {};
      this.title = options.title || '';
      this.emptyMessage = options.emptyMessage || 'No data.';
      this.commandType = options.commandType;
      this.secondScreen = !!options.secondScreen;
      // Forwarded to each row so a status change can load its mail template
      // into the host's reply composer (Sales Inbox) instead of a popup.
      this.onComposeTemplate = options.onComposeTemplate || null;
      this.statuses = [];
      this.displayOrderByStatus = {};
      // Multiselect: enabled only where a composer sink exists (the Sales
      // Inbox), which is also where a bulk template mail can be sent. Owns the
      // selected purchaseOrderId map shared (by reference) with each row.
      this.selectable = !!this.onComposeTemplate;
      this.selectedIds = {};

      if (this.commandType) {
        App.request('orders:statuses', this.commandType).done(_.bind(function (statuses) {
          if (this.isDestroyed) { return; }
          var models = (statuses && statuses.models) || [];
          this.statuses = _.map(models, function (s) {
            return { id: s.get('statusConst'), label: s.get('status') };
          });
          // Build a status → displayOrder lookup. The backend carries an
          // explicit `displayOrder` on each status (see
          // entities/caccounts/orderstatus.js); if the payload happens to
          // omit it we fall back to the array index, which matches the
          // server's default ordering.
          this.displayOrderByStatus = {};
          _.each(models, _.bind(function (s, idx) {
            var explicit = s.get('displayOrder');
            var hasExplicit = explicit !== undefined && explicit !== null && explicit !== '';
            var order = hasExplicit ? Number(explicit) : idx;
            if (isNaN(order)) { order = idx; }
            this.displayOrderByStatus[s.get('statusConst')] = order;
          }, this));

          // Re-render so children pick up the new statuses (dropdown
          // options) AND the viewComparator now has a populated lookup
          // map. Calling render() on a CompositeView destroys the current
          // children and rebuilds them using viewComparator ordering.
          this.render();
        }, this));
      }
    },

    childViewOptions: function () {
      return {
        statuses: this.statuses,
        secondScreen: this.secondScreen,
        onComposeTemplate: this.onComposeTemplate,
        selectable: this.selectable,
        selectedIds: this.selectedIds,
        onToggleSelect: _.bind(this.onToggleSelect, this)
      };
    },

    templateHelpers: function () {
      // Only surface the selection UI when the section actually has rows.
      var hasRows = !!(this.collection && this.collection.length);
      return {
        title: this.title,
        selectable: this.selectable && hasRows,
        selectedCount: this.selectedCount(),
        statuses: this.statuses,
        templates: this.selectable ? customerTemplates() : []
      };
    },

    events: {
      'change .selall-cb': 'onSelectAll',
      'change .cmd-bulk-status': 'onBulkStatus',
      'change .cmd-bulk-tpl': 'onBulkTemplate',
      'click .cmd-bulk-clear': 'onClearSelection'
    },

    onRender: function () {
      this.$el.toggleClass('selectable', this.selectable);
      this.updateBulkBar();
    },

    // ---- selection state -------------------------------------------------
    selectedModels: function () {
      var ids = this.selectedIds;
      return this.collection ? this.collection.filter(function (m) {
        return !!ids[m.get('purchaseOrderId')];
      }) : [];
    },

    // Count against the live collection so a removed row can't leave a stale
    // id inflating the total.
    selectedCount: function () {
      return this.selectedModels().length;
    },

    // A row's checkbox toggled — update the shared map and refresh the bulk
    // bar in place (no re-render, so the other rows' checkboxes stay put).
    onToggleSelect: function (model, checked) {
      var id = model.get('purchaseOrderId');
      if (checked) { this.selectedIds[id] = true; } else { delete this.selectedIds[id]; }
      this.updateBulkBar();
    },

    // Reflect the current selection onto the bulk bar without a re-render.
    updateBulkBar: function () {
      var count = this.selectedCount();
      var total = this.collection ? this.collection.length : 0;
      this.$('.cmd-bulk-count').text(count ? (count + ' selected') : 'Select');
      this.$('.cmd-bulk').toggleClass('on', count > 0);
      this.$('.selall-cb').prop('checked', total > 0 && count === total);
    },

    onSelectAll: function (e) {
      var checked = $(e.currentTarget).is(':checked');
      this.selectedIds = {};
      if (checked && this.collection) {
        this.collection.each(function (m) {
          this.selectedIds[m.get('purchaseOrderId')] = true;
        }, this);
      }
      // Re-render so every row's checkbox reflects the new selection.
      this.render();
    },

    onClearSelection: function () {
      this.selectedIds = {};
      this.render();
    },

    // ---- bulk actions ----------------------------------------------------
    // Change the status of every selected item, then — exactly like a single
    // row's onStatusChange — refresh each (fetch + list data / trackings) and,
    // if the new status is configured to send a templated mail, open ONE
    // template covering all selected items. Selection is kept throughout.
    onBulkStatus: function (e) {
      var $sel = $(e.currentTarget);
      var newStatus = $sel.val();
      $sel.val('');
      if (!newStatus) { return; }

      var models = this.selectedModels();
      if (!models.length) { return; }

      var self = this;
      // Only the items that aren't already at the target status actually change.
      var toChange = _.filter(models, function (m) { return m.get('status') !== newStatus; });
      if (!toChange.length) { return; }

      this.$el.addClass('bulk-saving');
      var statusPromises = _.map(toChange, function (m) { return m.updateStatus(newStatus); });

      $.when.apply($, statusPromises).done(function () {
        // Refresh every selected order (status + trackings) before composing the
        // mail so the template's per-order data is up to date. Mirrors the
        // single-row flow's fetch() + fetchListData().
        var refresh = _.map(models, function (m) {
          m.fetch();
          return m.fetchListData().done(function (data) {
            var listData = App.request('order:parse-list-data', data);
            m.set('trackings', listData.trackings);
          });
        });
        $.when.apply($, refresh).always(function () {
          if (self.isDestroyed) { return; }
          self.maybeSendStatusMail(newStatus, models);
        });
      }).always(function () {
        if (!self.isDestroyed) { self.$el.removeClass('bulk-saving'); }
      });
    },

    // If the target status carries a `sendMail` params blob (same config the
    // single-row status change reads), run the shared check then open ONE
    // template for all selected items.
    maybeSendStatusMail: function (newStatus, models) {
      var self = this;
      App.request('orders:statuses', this.commandType).done(function (statuses) {
        var statusModel = null;
        _.each(statuses.models, function (item) {
          if (item.get('statusConst') === newStatus) { statusModel = item; }
        });
        if (!statusModel || !statusModel.get('params')) { return; }

        var params;
        try { params = JSON.parse(statusModel.get('params')); }
        catch (err) { params = null; }
        if (!params) { return; }

        OrdersMailsUtils.check(models, params).done(function () {
          if (params.type !== 'sendMail') { return; }
          var template = params.params && params.params.type;
          if (!template) { return; }

          var configTemplate = null;
          _.each(Settings.configValue('mails.templates'), function (t) {
            if (t.template === template) { configTemplate = t; }
          });
          if (configTemplate) { self.sendTemplateMail(configTemplate, models); }
        });
      });
    },

    // Send ONE mail template covering every selected item. OrdersMailsUtils
    // groups same-customer orders into a single email (merged attachments +
    // template data), handed to the reply composer via onComposeTemplate.
    onBulkTemplate: function (e) {
      var $sel = $(e.currentTarget);
      var template = $sel.val();
      $sel.val('');
      if (!template) { return; }

      var models = this.selectedModels();
      if (!models.length) { return; }

      var configTemplate = null;
      _.each(Settings.configValue('mails.templates'), function (t) {
        if (t.template === template) { configTemplate = t; }
      });
      if (configTemplate) { this.sendTemplateMail(configTemplate, models); }
    },

    // Hand a configured template + the given orders to the reply composer via
    // onComposeTemplate (falling back to the EmailView popup), mirroring the
    // per-row sendTemplateMail but for an array of orders.
    sendTemplateMail: function (configTemplate, models) {
      var composer = this.onComposeTemplate;
      if (composer) {
        OrdersMailsUtils.sendMail(models, configTemplate, null, null, function (emailOptions) {
          if (!composer(emailOptions)) { OrdersMailsUtils.sendMail(models, configTemplate); }
        });
      } else {
        OrdersMailsUtils.sendMail(models, configTemplate);
      }
    },

    // Marionette renders the empty view inside childViewContainer when
    // the collection is empty — perfect for our "No offers" / "No orders"
    // fallback without any conditional template logic.
    getEmptyView: function () {
      return buildEmptyView(this.emptyMessage);
    }
  });
});

